/**
 * Returns the scope-filtered option sets for campaign form dropdowns.
 * When a permission is specified, only groups that carry that permission contribute
 * to the allowed scope — so "create" dropdowns show only what the user can create in,
 * not everything they can view.
 */
import { useMemo } from 'react';
import { ALL_SUPPORTED_COUNTRIES } from 'src/constants';
import {
  PermissionScopeBusinessLines as ALL_BUSINESS_LINES,
  PermissionScopePublishers as ALL_PUBLISHERS,
} from 'src/store/constants';
import type { PermissionAssignmentDto } from 'src/store/generated/leela-api-generated';
import { type UseMyPermissionsOptions, useMyPermissions } from './useMyPermissions';

type Permission =
  | 'VIEW_CAMPAIGNS'
  | 'MANAGE_CAMPAIGNS'
  | 'APPROVE_ASSETS'
  | 'APPROVE_WORKFLOWS'
  | 'MANAGE_BLOCKLISTS';

export interface AllowedScope {
  readonly countries: readonly string[];
  readonly publishers: readonly string[];
  readonly businessLines: readonly string[];
  readonly businessUnitIds: readonly string[];
}

/**
 * A group's empty `countries` means "every country of its business unit"; returns those unit
 * countries, or empty (unbounded) when an older backend omits `businessUnitCountries`.
 */
function getAssignmentCountries (assignment: PermissionAssignmentDto): readonly string[] {
  if (assignment.countries.length > 0) {
    return assignment.countries;
  }
  return assignment.businessUnitCountries ?? [];
}

export function useAllowedScope (
  permission?: Permission,
  options: UseMyPermissionsOptions = {},
): AllowedScope & { isLoading: boolean } {
  const { isPlatformAdmin, isBusinessUnitAdmin, businessUnitAdminOf, assignments, isLoading } = useMyPermissions(options);

  const scope = useMemo((): AllowedScope => {
    // Platform admins see everything regardless of permission filter
    if (isPlatformAdmin) {
      return {
        countries: [...ALL_SUPPORTED_COUNTRIES],
        publishers: [...ALL_PUBLISHERS],
        businessLines: [...ALL_BUSINESS_LINES],
        businessUnitIds: Array.from(new Set(assignments.map(a => a.businessUnitId))),
      };
    }

    // BU admins have full access within their BU — countries from their BU data,
    // all publishers and business lines. They can also hold groups in other BUs.
    if (isBusinessUnitAdmin) {
      const buCountries = Array.from(new Set(businessUnitAdminOf.flatMap(bu => bu.countries as string[])));
      return {
        countries: buCountries.length > 0 ? buCountries : [...ALL_SUPPORTED_COUNTRIES],
        publishers: [...ALL_PUBLISHERS],
        businessLines: [...ALL_BUSINESS_LINES],
        businessUnitIds: Array.from(new Set([
          ...businessUnitAdminOf.map(bu => bu.id),
          ...assignments.map(a => a.businessUnitId),
        ])),
      };
    }

    // No assignments at all (pre-seeding / no groups exist yet) — show everything
    // to preserve current behavior until CR E seeds groups
    if (assignments.length === 0) {
      return {
        countries: [...ALL_SUPPORTED_COUNTRIES],
        publishers: [...ALL_PUBLISHERS],
        businessLines: [...ALL_BUSINESS_LINES],
        businessUnitIds: [],
      };
    }

    // Filter assignments to only those carrying the requested permission
    const filtered: readonly PermissionAssignmentDto[] = permission
      ? assignments.filter(a => a.permissions?.includes(permission))
      : assignments;

    if (filtered.length === 0) {
      return { countries: [], publishers: [], businessLines: [], businessUnitIds: [] };
    }

    const assignmentCountries = filtered.map(getAssignmentCountries);
    const countries = assignmentCountries.some(c => c.length === 0)
      ? [...ALL_SUPPORTED_COUNTRIES]
      : Array.from(new Set(assignmentCountries.flat()));

    const publishers = filtered.some(a => a.publishers.length === 0)
      ? [...ALL_PUBLISHERS]
      : Array.from(new Set(filtered.flatMap(a => a.publishers)));

    const businessLines = filtered.some(a => a.businessLines.length === 0)
      ? [...ALL_BUSINESS_LINES]
      : Array.from(new Set(filtered.flatMap(a => a.businessLines)));

    const businessUnitIds = Array.from(new Set(filtered.map(a => a.businessUnitId)));

    return { countries, publishers, businessLines, businessUnitIds };
  }, [isPlatformAdmin, isBusinessUnitAdmin, businessUnitAdminOf, assignments, permission]);

  return { ...scope, isLoading };
}
